import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { prefersReducedMotion, isLowPowerDevice } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.videoAutoplay) {
      this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
      // Multi-slide carousels emit slideshow:select on init; single-slide setups do not.
      // Slideshow.connectedCallback is async, so retry after paint / macrotask.
      requestAnimationFrame(() => {
        this.#syncCarouselVideoPlayback();
        this.#armPreloadNextVideo();
        setTimeout(() => this.#syncCarouselVideoPlayback(), 0);
      });

      document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

      // Grid presentation: slideshow is carousel-only below 750px. Re-sync when that flips
      // so videos pause when the slideshow hides and play when it becomes the visible UI.
      if (this.presentation === 'grid') {
        const mobileCarouselQuery = window.matchMedia('(max-width: 749px)');
        mobileCarouselQuery.addEventListener('change', this.#handleViewportPresentationChange, { signal });
      }

      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      reducedMotionQuery.addEventListener('change', this.#handleViewportPresentationChange, { signal });
    }
  }

  #controller = new AbortController();

  #selectGeneration = 0;

  /** Speculative preload waits for the first image so it never races the hero. */
  #preloadArmed = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Whether muted carousel video autoplay is enabled.
   * @returns {boolean}
   */
  get videoAutoplay() {
    return this.dataset.videoAutoplay === 'true';
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Plays the active carousel slide video when autoplay is enabled.
   */
  #handleSlideshowSelect = () => {
    this.#selectGeneration += 1;
    this.#syncCarouselVideoPlayback();
    // Init select can fire before the first arm attempt sees slides.
    this.#armPreloadNextVideo();
    this.#maybePreloadNextVideo();
  };

  /**
   * Re-syncs playback when grid↔carousel visibility or reduced-motion preference changes.
   * Waits a frame so CSS display changes from the breakpoint are applied first.
   */
  #handleViewportPresentationChange = () => {
    requestAnimationFrame(() => {
      this.#syncCarouselVideoPlayback();
      this.#maybePreloadNextVideo();
    });
  };

  /**
   * Re-evaluates speculative preload when the tab becomes visible again.
   */
  #handleVisibilityChange = () => {
    if (!document.hidden) this.#maybePreloadNextVideo();
  };

  /**
   * Arms speculative preload after the first gallery image finishes loading.
   * Select/visibility/viewport handlers call maybe-preload, but it no-ops until
   * this flag is set so preload never competes with the hero image.
   */
  #armPreloadNextVideo() {
    if (this.#preloadArmed) return;
    if (this.dataset.preloadNextVideo !== 'true' || !this.videoAutoplay) return;

    const slideshow = this.slideshow;
    if (!slideshow?.slides?.length) return;

    const { signal } = this.#controller;
    const firstImage = slideshow.querySelector('slideshow-slide img');

    const arm = () => {
      this.#preloadArmed = true;
      this.#maybePreloadNextVideo();
    };

    if (!(firstImage instanceof HTMLImageElement)) {
      arm();
      return;
    }

    if (firstImage.complete && firstImage.naturalWidth > 0) {
      arm();
      return;
    }

    firstImage.addEventListener('load', arm, { once: true, signal });
    firstImage.addEventListener('error', arm, { once: true, signal });
  }

  /**
   * Prepares the next unprepared video ahead of the selected slide, skipping
   * intervening images without wrapping around. At most one speculative video
   * at a time; only the visible carousel is eligible.
   */
  #maybePreloadNextVideo() {
    if (!this.#preloadArmed) return;
    if (this.dataset.preloadNextVideo !== 'true' || !this.videoAutoplay) return;
    if (document.hidden || prefersReducedMotion() || isLowPowerDevice()) return;
    if (!this.#connectionAllowsPreload()) return;

    const slideshow = this.slideshow;
    if (!slideshow || !this.#isElementVisible(slideshow)) return;

    const slides = slideshow.slides;
    if (!slides?.length) return;

    if (slideshow.querySelector('deferred-media[data-media-prepared]:not([data-media-loaded])')) return;

    const current = slideshow.current ?? 0;
    for (let index = current + 1; index < slides.length; index += 1) {
      const media = slides[index].querySelector('deferred-media:not([data-media-loaded]):not([data-media-prepared])');
      if (!media) continue;

      /** @type {HTMLElement & { prepareContent?: () => boolean }} */ (media).prepareContent?.();
      return;
    }
  }

  /**
   * Whether the current network conditions permit speculative preloading.
   * Missing connection information permits preparation.
   * @returns {boolean}
   */
  #connectionAllowsPreload() {
    /** @type {{ saveData?: boolean, effectiveType?: string } | undefined} */
    const connection = navigator.connection;

    if (!connection || typeof connection !== 'object') return true;
    if (connection.saveData === true) return false;

    return connection.effectiveType !== 'slow-2g' && connection.effectiveType !== '2g';
  }

  /**
   * Play muted video in the active carousel slide; pause other slideshow videos.
   * No-ops when the slideshow is hidden (e.g. desktop grid presentation).
   */
  #syncCarouselVideoPlayback() {
    if (!this.videoAutoplay) return;

    if (prefersReducedMotion()) {
      this.#pauseSlideshowVideos();
      return;
    }

    const slideshow = this.slideshow;
    if (!slideshow || !this.#isElementVisible(slideshow)) {
      this.#pauseSlideshowVideos();
      return;
    }

    const activeSlide =
      slideshow.slides?.[slideshow.current] ?? slideshow.querySelector('slideshow-slide[aria-hidden="false"]');

    const deferredMedias = slideshow.querySelectorAll('deferred-media');

    for (const media of deferredMedias) {
      if (!(media instanceof HTMLElement)) continue;

      if (activeSlide?.contains(media)) {
        this.#playDeferredMedia(media);
      } else {
        // Leaving the slide clears intentional pause so return visits can autoplay.
        media.removeAttribute('data-user-paused');
        /** @type {HTMLElement & { pauseMedia?: () => void }} */ (media).pauseMedia?.();
      }
    }
  }

  #pauseSlideshowVideos() {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    for (const media of slideshow.querySelectorAll('deferred-media')) {
      /** @type {HTMLElement & { pauseMedia?: () => void }} */ (media).pauseMedia?.();
    }
  }

  /**
   * @param {HTMLElement & { loadContent?: (focus?: boolean) => void, playMedia?: () => Promise<void> | undefined, pauseMedia?: () => void }} media
   */
  async #playDeferredMedia(media) {
    const generation = this.#selectGeneration;
    const userPaused = media.hasAttribute('data-user-paused');
    const alreadyPlaying =
      !!/** @type {any} */ (media).isPlaying && media.querySelector('video')?.paused === false;

    // Respect intentional pause; do not restart an already-playing video (avoids
    // play/pause races with the toggle button while autoplay is enabled).
    if (userPaused || alreadyPlaying) return;

    if (!media.getAttribute('data-media-loaded')) {
      // Avoid stealing focus when autoplaying on slide change.
      media.loadContent?.(false);
    }

    // Single play owner: loadContent only autoplays elements that still have
    // the autoplay attribute. Prepared videos strip it, so playMedia starts them.
    const pendingPlay = media.playMedia?.();
    if (!pendingPlay || typeof pendingPlay.then !== 'function') return;

    try {
      await pendingPlay;
    } catch {
      // play() rejects on interruption / autoplay policy; DeferredMedia restores the poster.
      return;
    }

    // Guard against delayed playback after rapid slide changes.
    if (generation !== this.#selectGeneration) {
      /** @type {HTMLElement & { pauseMedia?: () => void }} */ (media).pauseMedia?.();
    }
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  #isElementVisible(element) {
    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }

    return /** @type {HTMLElement} */ (element).offsetParent !== null;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
